import { Component, Input, signal, computed, inject, effect, OnInit, OnDestroy, HostListener, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { toast } from 'ngx-sonner';
import { ButtonComponent } from '../../../../../../shared/components/ui/button/button.component';
import { IconComponentV2 } from '../../../../../../shared/components/icon-component/icon.component';
import { ToggleComponent } from '../../../../../../shared/components/ui/toggle/toggle.component';
import { ColorPickerDirective } from 'ngx-color-picker';
import { WhitelabelChatService } from '../../../../../../core/services/whitelabel-chat.service';
import { finalize } from 'rxjs';
import { 
  ChatbotEmbedConfig, 
  DEFAULT_CONFIG, 
  CONFIG_SECTIONS,
  ConfigField
} from './config.constants';
import { mapApiToInternalConfig, mapInternalToApiConfig } from '../../../../../../core/utils/chatbot-config-mapper';

@Component({
  selector: 'app-config',
  standalone: true,
  imports: [CommonModule, FormsModule, ButtonComponent, IconComponentV2, ToggleComponent, ColorPickerDirective],
  templateUrl: './config.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './config.component.scss',
})
export class ConfigComponent implements OnInit, OnDestroy {
  @Input() botId: number | null = null;
  
  sections = CONFIG_SECTIONS;

  source = signal('');
  userId = signal('');
  
  embedConfig = signal<ChatbotEmbedConfig>(JSON.parse(JSON.stringify(DEFAULT_CONFIG)));
  openSections = signal<Set<string>>(new Set());
  isSaving = signal(false);
  pid = signal<number | null>(null);

  private whitelabelService = inject(WhitelabelChatService);

  constructor() {
    effect(() => {
      const config = this.embedConfig();
      // Sync with the floating chatbot preview if it exists
      const widget = document.querySelector('prism-chatbot') as any;
      if (widget && typeof widget.setFullConfig === 'function') {
        widget.setFullConfig(config);
      }
    });
  }

  ngOnInit() {
    if (this.botId) {
      this.loadConfig(this.botId);
    }
  }

  ngOnDestroy() {
    // Auto-save on destroy (normal SPA navigation)
    if (this.botId) {
      this.saveConfig(false);
    }
  }

  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent) {
    // Standard HttpClient requests are aborted on reload.
    // Use the reliable keepalive fetch for browser-level unload events.
    const chatbot_id = this.botId;
    if (chatbot_id) {
      const payload = mapInternalToApiConfig(this.embedConfig(), chatbot_id, this.source());
      this.whitelabelService.saveAssistantIframeConfigSync(payload, this.pid() ?? undefined);
    }
  }

  private loadConfig(chatbotId: number) {
    this.whitelabelService.getAssistantIframeConfig(chatbotId).subscribe({
      next: (res) => {
        const configData = Array.isArray(res?.data) ? res.data[0] : res?.data;
        if (configData && typeof configData !== 'boolean') {
          const configId = configData.pid || configData.id || (typeof configData !== 'object' ? configData : null);
          if (configId && !isNaN(Number(configId)) && typeof configId !== 'boolean') {
            this.pid.set(Number(configId));
          }
          this.source.set(configData.source || '');
          const mapped = mapApiToInternalConfig(configData, DEFAULT_CONFIG);
          this.embedConfig.set(mapped);
        } else {
          this.pid.set(null);
          this.source.set('');
          this.embedConfig.set(JSON.parse(JSON.stringify(DEFAULT_CONFIG)));
          // Auto-save default config if none exists to initialize the pid
          this.saveConfig(false);
        }
      },
      error: (err) => {
        console.error('Failed to load chatbot config', err);
      }
    });
  }

  toggleSection(section: string) {
    this.openSections.update(set => {
      const next = new Set(set);
      if (next.has(section)) next.delete(section);
      else next.add(section);
      return next;
    });
  }

  isSectionOpen(section: string) {
    return this.openSections().has(section);
  }

  getValue(path: string) {
    const parts = path.split('.');
    let current: any = this.embedConfig();
    for (const part of parts) {
      if (current === undefined || current === null) return undefined;
      current = current[part];
    }
    return current;
  }


  updateConfig(path: string, value: any) {
    // If it's a color field, ensure it's a valid hex before updating
    if (path.includes('theme.') || path.includes('backgroundColor') || path.includes('textColor')) {
      if (typeof value === 'string' && !value.startsWith('#')) {
        value = `#${value}`;
      }
    }

    this.embedConfig.update(config => {
      const parts = path.split('.');
      const next: any = { ...config };
      let current = next;
      for (let i = 0; i < parts.length - 1; i++) {
        current[parts[i]] = { ...current[parts[i]] };
        current = current[parts[i]];
      }
      current[parts[parts.length - 1]] = value;
      return next;
    });
  }

  getDynamicMin(field: ConfigField): number | null {
    if (field.path === 'layout.chatPanel.widthPx') {
      const compactWidth = this.getValue('layout.chatPanel.widthCompactPx') || 0;
      // Expanded width must be at least the compact width, AND at least 600px 
      // (so that a 280px sidebar leaves at least 320px for the chat)
      const dynamicMin = Math.max(field.min ?? 600, compactWidth);
      return Math.min(dynamicMin, field.max ?? 1200);
    }
    return field.min ?? null;
  }

  getDynamicMax(field: ConfigField): number | null {
    if (field.path === 'layout.chatPanel.widthCompactPx') {
      const fullWidth = this.getValue('layout.chatPanel.widthPx') || 0;
      // Compact width cannot exceed the Expanded width
      const dynamicMax = Math.min(field.max ?? 800, fullWidth);
      return Math.max(dynamicMax, field.min ?? 320);
    }
    return field.max ?? null;
  }

  addSuggestionPrompt() {
    this.embedConfig.update(c => ({
      ...c,
      behavior: {
        ...c.behavior,
        suggestionPrompts: [...c.behavior.suggestionPrompts, '']
      }
    }));
  }

  removeSuggestionPrompt(index: number) {
    this.embedConfig.update(c => ({
      ...c,
      behavior: {
        ...c.behavior,
        suggestionPrompts: c.behavior.suggestionPrompts.filter((_, i) => i !== index)
      }
    }));
  }

  updateSuggestionPrompt(index: number, value: string) {
    this.embedConfig.update(c => {
      const prompts = [...c.behavior.suggestionPrompts];
      prompts[index] = value;
      return {
        ...c,
        behavior: { ...c.behavior, suggestionPrompts: prompts }
      };
    });
  }

  generatedSnippet = computed(() => {
    const id = this.botId ?? '1';
    const src = this.source();
    const uid = this.userId();
    const origin = window.location.origin;
    
    let snippet = `<prism-chatbot agent-id="${id}" source="${src}" user-id="${uid}"></prism-chatbot>\n<script src="${origin}/prism-chatbot-widget.js" async></script>`;

    return snippet;
  });

  highlightedSnippet = computed(() => {
    const snippet = this.generatedSnippet();
    const primary = this.embedConfig().theme.primary;
    
    // Simple syntax highlighting regex
    return snippet
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      // Highlight Tags
      .replace(/(&lt;\/?[a-z0-9-]+)/gi, `<span style="color: ${primary}; font-weight: 600;">$1</span>`)
      // Highlight Attributes
      .replace(/ ([a-z0-9-]+)=/gi, ` <span style="color: #94a3b8; font-style: italic;">$1</span>=`)
      // Highlight Values
      .replace(/"([^"]*)"/g, `<span style="color: #4ade80;">"$1"</span>`)
      // Highlight Comments
      .replace(/(&lt;!--[\s\S]*?--&gt;)/g, `<span style="color: #64748b; font-style: italic;">$1</span>`);
  });

  copySnippet() {
    if (!this.source()) {
      toast.error('Please enter a source');
      return;
    }
    navigator.clipboard.writeText(this.generatedSnippet()).then(() => {
      toast.success('Snippet copied to clipboard');
    }).catch(err => {
      console.error('Failed to copy snippet: ', err);
      toast.error('Failed to copy snippet');
    });
  }

  saveConfig(showToast = true) {
    const pid = this.pid();
    const chatbot_id = this.botId;
    if (!chatbot_id) {
      toast.error('Chatbot ID missing');
      return;
    }

    const payload = mapInternalToApiConfig(this.embedConfig(), chatbot_id, this.source());

    this.isSaving.set(true);
    this.whitelabelService.saveAssistantIframeConfig(payload, showToast, pid ? pid : undefined)
      .pipe(finalize(() => this.isSaving.set(false)))
      .subscribe({
        next: (res: any) => {
          // Update pid if it returned from server (especially on first save)
          const data = Array.isArray(res?.data) ? res.data[0] : res?.data;
          
          if (data && typeof data !== 'boolean') {
            // Ensure we extract the pid if it's an object, or use it directly if it's the ID
            const newId = data.pid || data.id || (typeof data !== 'object' ? data : null);
            
            if (newId && !isNaN(Number(newId)) && typeof newId !== 'boolean') {
              this.pid.set(Number(newId));
            }
          }
        },
        error: (err) => {
          console.error('Failed to save config', err);
        }
      });
  }
}
